import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ProductsState } from 'src/app/shared/state/ProductsState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { AssetManagementState } from '../../../../shared/state/AssetManagementState';
import { generateMRTLine } from '../../AssetManagementTestHelper';
import { MRT } from '../../models/MRT';
import { ActionType, DispositionType, enforceNumberField, getActionType, getDispositionType } from '../../models/SpecLine';
import { AssetManagementService } from '../../services/asset-management.service';
import { TranslationState } from '../../../../shared/state/TranslationState';

@Component({
  selector: 'app-action-details',
  templateUrl: './action-details.component.html',
  styleUrls: ['./action-details.component.scss'],
  standalone: false
})
export class ActionDetailsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();
  @Input() id: string;
  @Input() limit: number;
  @Input() editMode: boolean;
  @Input() formGroup: FormGroup;
  @Input() limitTranslationKey: string;
  @Input() limitFormControlName: string;
  @Input() isDefault: boolean;

  //Remove this line when fixing temporory code
  private readonly staticApplicationValues = [
    '',
    'PICKUPDELI',
    'LINEHAUL',
    'SEVEREUSE',
    'OTR',
    'LIGHTTRUCK',
    'LONGUEDIST',
    'RÉGIONALE',
    'HORS ROUTE'
  ];
  actionValues: string[];
  dispositionValues: string[];
  disposalCodeValues: string[];
  applicationValues = [''];
  axleValues = [''];
  treadsValues: string[];
  mrtToValidate: MRT;
  ntrAgeIsNull = false;
  viewMode: string;
  dispositionSub: Subscription;
  actionSub: Subscription;

  public translationService = inject(TranslationService);
  public assetManagementService = inject(AssetManagementService);
  public store = inject(Store);

  constructor() {}

  ngOnInit(): void {
    this.actionValues = this.translationService.translateList([ActionType.REJECT, ActionType.DOWNGRADE]);
    this.dispositionValues = this.translationService.translateList([DispositionType.RETURN, DispositionType.SCRAP]);
    this.disposalCodeValues = this.store.selectSnapshot(ProductsState.products).disposalCodes;
    this.formGroup.get('disposalCode').markAsTouched();
    // remove this line when fixing temporory code
    this.applicationValues.push(...this.compareStaticApplications(this.store.selectSnapshot(ProductsState.products).applications));
    // Add this code back:
    // this.applicationValues.push(...this.store.selectSnapshot(ProductsState.products).applications);
    this.axleValues.push(...this.store.selectSnapshot(ProductsState.products).axles);
    this.treadsValues = this.store.selectSnapshot(ProductsState.products).treads;
    this.viewMode = this.editMode ? 'edit-component' : 'view-component';

    // this.removeErrorOnChanges();
    this.mrtToValidate = generateMRTLine();
    this.assetManagementService.getCurrentApplication().subscribe((x) => {
      if (this.store.selectSnapshot(AssetManagementState.mrtSpecifications)) {
        const mrtMap = this.store.selectSnapshot(AssetManagementState.mrtSpecifications);
        this.mrtToValidate = mrtMap.get(x) ? mrtMap.get(x) : generateMRTLine();
      }
      this.ntrAgeIsNull = (this.id === 'ntr' && this.mrtToValidate.ntr === null) || (this.id === 'age' && this.mrtToValidate.age === null);
    });
    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.prepopulateForm();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('editMode' in changes) {
      this.viewMode = changes.editMode.currentValue ? 'edit-component' : 'view-component';
    }

    if ('formGroup' in changes) {
      this.unsubscribeFromValueChanges();
      this.removeErrorOnChanges();
    }
  }
  removeErrorOnChanges() {
    if (!this.shouldDisplayActionDetails()) {
      return;
    }
    this.dispositionSub = this.formGroup.get('disposition').valueChanges.subscribe((x) => {
      if (getDispositionType(x) === 'dispositionReturnText') {
        this.formGroup.get('disposalCode').clearValidators();
      } else if (this.formGroup.get('action').value === 'REJECT' && x === 'SCRAP') {
        this.formGroup.get('disposalCode').addValidators(Validators.required);
      }
      this.formGroup.get('treads').clearValidators();
      this.formGroup.get('disposalCode').updateValueAndValidity();
      this.formGroup.get('treads').updateValueAndValidity();
    });

    this.actionSub = this.formGroup.get('action').valueChanges.subscribe((x) => {
      if (getActionType(x) === 'actionDowngradeText') {
        this.formGroup.get('disposition').clearValidators();
        this.formGroup.get('disposalCode').clearValidators();
        this.formGroup.get('treads').addValidators(Validators.required);
      }
      if (getActionType(x) === 'actionRejectText') {
        this.formGroup.get('treads').clearValidators();
        this.formGroup.get('disposition').addValidators(Validators.required);
        if (getDispositionType(this.formGroup.get('disposition').value) === 'dispositionReturnText') {
          this.formGroup.get('disposalCode').clearValidators();
        } else if (getDispositionType(this.formGroup.get('disposition').value) === 'dispositionScrapText') {
          this.formGroup.get('disposalCode').addValidators(Validators.required);
        }
      }
      this.formGroup.get('disposition').updateValueAndValidity();
      this.formGroup.get('disposalCode').updateValueAndValidity();
      this.formGroup.get('treads').updateValueAndValidity();
    });
  }

  shouldDisplayActionDetails() {
    return !this.id.endsWith('1');
  }

  checkValue(event: any) {
    if (this.id === 'ntr') {
      if (event.target.value.length > 1) {
        this.formGroup.get('limit').patchValue(event.target.value.slice(0, 1));
      }
    } else if (this.id === 'age') {
      if (event.target.value.length > 3) {
        this.formGroup.get('limit').patchValue(event.target.value.slice(0, 3));
      }
    } else {
      if (event.target.value.length > 2) {
        this.formGroup.get('totalRepairs').patchValue(event.target.value.slice(0, 2));
      }
    }
  }

  private unsubscribeFromValueChanges(): void {
    if (this.dispositionSub) {
      this.dispositionSub.unsubscribe();
    }
    if (this.actionSub) {
      this.actionSub.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeFromValueChanges();
  }

  protected readonly ActionType = ActionType;
  protected readonly DispositionType = DispositionType;
  protected readonly enforceNumberField = enforceNumberField;

  // remove this method when fixing temporory code
  compareStaticApplications(productApplications: string[]): string[] {
    const matchedValues: string[] = [];
    this.staticApplicationValues.forEach((x) => {
      if (productApplications.includes(x)) {
        matchedValues.push(x);
      }
    });
    return matchedValues;
  }

  prepopulateForm(): void {
    this.actionValues = this.translationService.translateList([ActionType.REJECT, ActionType.DOWNGRADE]);
    this.dispositionValues = this.translationService.translateList([DispositionType.RETURN, DispositionType.SCRAP]);
  }

  protected readonly getDispositionType = getDispositionType;
  protected readonly getActionType = getActionType;
}
